import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, AtSign } from "lucide-react";
import { api, formatErr, timeAgo, plural } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";
import VoteButtons from "@/components/VoteButtons";

export default function ThreadPage() {
  const { slug, threadId } = useParams();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");

  useEffect(() => { api.get(`/threads/${threadId}`).then((r) => setThread(r.data)).catch(() => setThread(false)); }, [threadId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/threads/${threadId}/comments`, { text });
      setThread((t) => ({ ...t, comments: [...t.comments, data], comments_count: t.comments_count + 1 }));
      setText("");
    } catch (err) { toast.error(formatErr(err)); }
  };

  if (thread === false) return <div className="p-16 text-center text-gray-400" data-testid="thread-not-found">Thread not found.</div>;
  if (!thread) return <div className="p-16 text-center text-gray-500">Loading...</div>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 md:px-8 md:py-14" data-testid="thread-page">
      <Link to={`/c/${slug}`} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white" data-testid="back-to-community"><ArrowLeft className="h-4 w-4" /> c/{slug}</Link>
      <article className="mt-6 flex gap-4 rounded-md border border-[#2A2E37] bg-[#121418] p-6">
        <VoteButtons thread={thread} />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-400">@{thread.author.username} · {timeAgo(thread.created_at)}</p>
          <h1 className="mt-1 font-display text-xl font-semibold tracking-tight sm:text-2xl" data-testid="thread-title">{thread.title}</h1>
          {thread.body && <p className="mt-3 whitespace-pre-wrap text-sm text-gray-200 md:text-base" data-testid="thread-body">{thread.body}</p>}
          {thread.about_username && (
            <Link to={`/u/${thread.about_username}`} data-testid="thread-about-link"
              className="mt-4 inline-flex items-center gap-1 rounded-sm border border-[#2A2E37] bg-[#1C1F26] px-3 py-1 text-xs font-semibold text-gray-200 hover:bg-[#2A2E37]">
              <AtSign className="h-3 w-3" /> View {thread.about_username}'s page
            </Link>
          )}
        </div>
      </article>
      <h2 className="mt-10 font-display text-base font-semibold">{plural(thread.comments_count, "comment")}</h2>
      {user ? (
        <form onSubmit={submit} className="mt-4 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Join the discussion..." data-testid="thread-comment-input"
            className="flex-1 rounded-sm border border-[#2A2E37] bg-white/5 px-4 py-3 text-sm outline-none focus:border-white/40" />
          <button type="submit" data-testid="thread-comment-submit-btn" className="btn-neon rounded-sm px-5 text-sm font-semibold text-white">Reply</button>
        </form>
      ) : (
        <Link to="/login" className="mt-4 block text-sm text-[#FF2E4D] hover:underline" data-testid="thread-login-link">Log in to reply</Link>
      )}
      <div className="mt-6 space-y-5 border-l border-[#2A2E37] pl-5" data-testid="thread-comments">
        {thread.comments.map((c) => (
          <div key={c.id} className="flex gap-3 rise-in" data-testid="thread-comment">
            <Avatar user={c.author} size="h-8 w-8" />
            <div>
              <p className="text-xs text-gray-400"><span className="font-semibold text-gray-200">@{c.author.username}</span> · {timeAgo(c.created_at)}</p>
              <p className="text-sm text-gray-100">{c.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
